import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Sizes, Tabs } from '@sede-enterprise/shell-ds-react-framework';
import DownloadButton from 'src/UI/Download';
import { DOWNLOAD_MASTER_DATA } from 'src/constants/EUMarkets';
import { SystemAdminContainer } from './style';
import { Content } from '../../../UI/Grid';
import NominationGroups from './NominationGroups';
import Markets from './Markets';
import Locations from './Locations';
import Organizations from './Organizations';
import Contracts from './Contracts';

const FlowConfiguration = lazy(() => import('./Flows'));

const TABS = {
  NOMINATION_GROUPS: 'nomination-groups',
  CONTRACTS: 'contracts',
  MARKETS: 'markets',
  LOCATIONS: 'locations',
  ORGANIZATIONS: 'organizations',
  FLOW_CONFIGURATION: 'flow-configuration',
};

const renderRouteLabel = (label: string, route: string) => (
  <a
    href={route}
    onClick={(event) => event.preventDefault()}
    style={{ color: 'inherit', textDecoration: 'none' }}
  >
    {label}
  </a>
);

const SystemAdmin: React.FC = () => {
  const { tabName = 'markets' } = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<string>(tabName);

  useEffect(() => {
    setCurrentTab(tabName);
  }, [tabName]);

  const tabs = {
    [TABS.NOMINATION_GROUPS]: '/new-genesis/system-admin/nomination-groups',
    [TABS.CONTRACTS]: '/new-genesis/system-admin/contracts',
    [TABS.MARKETS]: '/new-genesis/system-admin/markets',
    [TABS.LOCATIONS]: '/new-genesis/system-admin/locations',
    [TABS.ORGANIZATIONS]: '/new-genesis/system-admin/organizations',
    [TABS.FLOW_CONFIGURATION]: '/new-genesis/system-admin/flow-configuration',
  };

  const handleTabChange = (activeKey: string) => {
    setCurrentTab(activeKey);
    navigate(tabs[activeKey]);
  };

  const renderComponent = (key: string, component: React.ReactNode) => {
    if (currentTab === key) {
      return <Suspense fallback={null}>{component}</Suspense>;
    }
    return null;
  };

  const masterTabs = [
    {
      children: renderComponent(TABS.MARKETS, <Markets />),
      key: TABS.MARKETS,
      label: renderRouteLabel('Markets', tabs[TABS.MARKETS]),
    },
    {
      children: renderComponent(TABS.LOCATIONS, <Locations />),
      key: TABS.LOCATIONS,
      label: renderRouteLabel('Locations', tabs[TABS.LOCATIONS]),
    },
    {
      children: renderComponent(TABS.ORGANIZATIONS, <Organizations />),
      key: TABS.ORGANIZATIONS,
      label: renderRouteLabel('Organizations', tabs[TABS.ORGANIZATIONS]),
    },
    {
      children: renderComponent(TABS.NOMINATION_GROUPS, <NominationGroups />),
      key: TABS.NOMINATION_GROUPS,
      label: renderRouteLabel('Nomination Groups', tabs[TABS.NOMINATION_GROUPS]),
    },
    {
      children: renderComponent(TABS.CONTRACTS, <Contracts />),
      key: TABS.CONTRACTS,
      label: renderRouteLabel('Contracts', tabs[TABS.CONTRACTS]),
    },
    {
      children: renderComponent(TABS.FLOW_CONFIGURATION, <FlowConfiguration />),
      key: TABS.FLOW_CONFIGURATION,
      label: renderRouteLabel('Flow Configuration', tabs[TABS.FLOW_CONFIGURATION]),
    },
  ];

  return (
    <Content style={{ padding: '0', backgroundColor: 'transparent' }}>
      <SystemAdminContainer>
        <Tabs
          activeKey={currentTab}
          onChange={handleTabChange}
          emphasis
          items={masterTabs}
          tabBarExtraContent={{
            right: (
              <DownloadButton
                downloadURL={DOWNLOAD_MASTER_DATA}
                method='GET'
                fileName='MasterData'
                fileType='xlsx'
                buttonProp={{
                  size: Sizes.Small,
                  variant: 'filled',
                  className: 'master-data-download',
                }}
              >
                Download All Master Data
              </DownloadButton>
            ),
          }}
        />
      </SystemAdminContainer>
    </Content>
  );
};

export default SystemAdmin;
